import React from 'react';
import { motion } from 'framer-motion';
import { Check, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';

const Pricing = ({ pricing, onBookClick }) => {
  if (!pricing || pricing.length === 0) {
    return null;
  }

  return (
    <section id="pricing" className="py-24 md:py-32 bg-[#0a0a0a]" data-testid="pricing-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase font-light mb-4">
            Investment
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#e5e5e5]">
            Our <span className="italic">Packages</span>
          </h2>
          <p className="text-[#a3a3a3] mt-4 max-w-2xl mx-auto">
            Choose the perfect package for your needs. All packages can be customized to suit your specific requirements.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {pricing.map((pkg, index) => (
            <motion.div
              key={pkg.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className={`relative p-8 md:p-10 transition-all duration-300 ${
                pkg.popular
                  ? 'bg-[#1a1a1a] border-2 border-[#d4af37] transform md:-translate-y-4'
                  : 'bg-[#121212] border border-[#262626] hover:border-[#d4af37]/50'
              }`}
              data-testid={`pricing-card-${pkg.id}`}
            >
              {/* Popular Badge */}
              {pkg.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#d4af37] px-6 py-2 flex items-center gap-2">
                  <Star className="w-4 h-4 text-[#0a0a0a]" />
                  <span className="text-[#0a0a0a] text-xs font-semibold tracking-wider uppercase">
                    Most Popular
                  </span>
                </div>
              )}

              {/* Package Name */}
              <div className="text-center mb-8">
                <h3 className="font-['Playfair_Display'] text-2xl text-[#e5e5e5] mb-2">
                  {pkg.name}
                </h3>
                <p className="text-[#a3a3a3] text-sm">{pkg.description}</p>
              </div>

              {/* Price */}
              <div className="text-center mb-8">
                <p className="text-4xl md:text-5xl font-['Playfair_Display'] text-[#d4af37]">
                  {pkg.price}
                </p>
                <p className="text-[#a3a3a3] text-sm mt-1">Starting Price</p>
              </div>

              {/* Features */}
              <ul className="space-y-4 mb-10">
                {pkg.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-3">
                    <Check className={`w-5 h-5 flex-shrink-0 mt-0.5 ${pkg.popular ? 'text-[#d4af37]' : 'text-[#d4af37]/70'}`} />
                    <span className="text-[#a3a3a3] text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA Button */}
              <Button
                onClick={onBookClick}
                className={`w-full py-6 rounded-none tracking-widest uppercase text-xs font-semibold transition-all ${
                  pkg.popular
                    ? 'bg-[#d4af37] text-[#0a0a0a] hover:bg-[#b3922d]'
                    : 'bg-transparent border border-[#d4af37] text-[#d4af37] hover:bg-[#d4af37] hover:text-[#0a0a0a]'
                }`}
                data-testid={`pricing-book-btn-${pkg.id}`}
              >
                Book This Package
              </Button>

              {/* Decorative Corner */}
              {pkg.popular && (
                <>
                  <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-[#d4af37]" />
                  <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-[#d4af37]" />
                </>
              )}
            </motion.div>
          ))}
        </div>

        {/* Custom Package Note */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center text-[#a3a3a3] mt-12"
        >
          Need a custom package?{' '}
          <button
            onClick={() => document.querySelector('#contact').scrollIntoView({ behavior: 'smooth' })}
            className="text-[#d4af37] hover:underline"
            data-testid="custom-package-link"
          >
            Contact us
          </button>{' '}
          to discuss your specific requirements.
        </motion.p>
      </div>
    </section>
  );
};

export default Pricing;
